// color-exempt-file — retokenisation Phase 6 ui-themes : getFilterColor(), palette par type de contenu, cas form/availability/quizz volontairement litteraux (branding par produit), cas date/all/conversations retokenise sur --primary (bug #3)
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@core/lib/utils";
import {
  Search,
  LayoutGrid,
  Table,
  Tag,
  Folder,
  X,
  Plus,
  CheckSquare,
  Calendar,
  ClipboardList,
  MessageSquare,
  ChevronDown,
} from "lucide-react";
import { FilterType, DashboardPoll, ContentTypeFilter } from "./types";
import { getStatusLabel } from "./utils";
import {
  getAllTags,
  createTag,
  Tag as TagType,
} from "@core/lib/storage/TagStorage";
import {
  getAllFolders,
  createFolder,
  Folder as FolderType,
} from "@core/lib/storage/FolderStorage";
import { useToast } from "@core/hooks/use-toast";

export type ViewMode = "grid" | "table";

interface DashboardFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filter: FilterType;
  onFilterChange: (filter: FilterType) => void;
  contentTypeFilter: ContentTypeFilter;
  onContentTypeFilterChange: (filter: ContentTypeFilter) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  selectedFolderId: string | null | undefined;
  onFolderChange: (folderId: string | null | undefined) => void;
  selectedIdsCount: number;
  onSelectAll: () => void;
  onClearSelection: () => void;
  hasItems: boolean;
  hideContentTypeFilter?: boolean;
  /** Produit hôte — adapte le placeholder recherche (form → "formulaire"). */
  productType?: string;
}

export const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  searchQuery,
  onSearchChange,
  filter,
  onFilterChange,
  contentTypeFilter,
  onContentTypeFilterChange,
  viewMode,
  onViewModeChange,
  selectedTags,
  onTagsChange,
  selectedFolderId,
  onFolderChange,
  selectedIdsCount,
  onSelectAll,
  onClearSelection,
  hasItems,
  hideContentTypeFilter = false,
  productType,
}) => {
  const { t } = useTranslation("polls");
  const searchPlaceholder =
    productType === "form"
      ? t("dashboard_filters.search_form_placeholder")
      : t("dashboard_filters.search_placeholder");
  const filters: FilterType[] = [
    "all",
    "draft",
    "active",
    "closed",
    "archived",
  ];
  const contentTypeFilters: {
    value: ContentTypeFilter;
    label: string;
    icon: React.ReactNode;
  }[] = [
    { value: "all", label: t("dashboard_filters.content_all"), icon: null },
    {
      value: "conversations",
      label: t("dashboard_filters.content_conversations"),
      icon: <MessageSquare className="w-4 h-4" />,
    },
    {
      value: "date",
      label: t("dashboard_filters.content_date"),
      icon: <Calendar className="w-4 h-4" />,
    },
    {
      value: "availability",
      label: t("dashboard_filters.content_availability"),
      icon: <Calendar className="w-4 h-4" />,
    },
    {
      value: "form",
      label: t("dashboard_filters.content_form"),
      icon: <ClipboardList className="w-4 h-4" />,
    },
  ];
  const [tags, setTags] = useState<TagType[]>(getAllTags());
  const [folders, setFolders] = useState<FolderType[]>(getAllFolders());
  const [showTagMenu, setShowTagMenu] = useState(false);
  const [showFolderMenu, setShowFolderMenu] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const { toast } = useToast();
  const tagMenuRef = useRef<HTMLDivElement>(null);
  const folderMenuRef = useRef<HTMLDivElement>(null);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  // Fermer les menus en cliquant ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tagMenuRef.current &&
        !tagMenuRef.current.contains(event.target as Node)
      ) {
        setShowTagMenu(false);
      }
      if (
        folderMenuRef.current &&
        !folderMenuRef.current.contains(event.target as Node)
      ) {
        setShowFolderMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleTag = (tagName: string) => {
    if (selectedTags.includes(tagName)) {
      onTagsChange(selectedTags.filter((t) => t !== tagName));
    } else {
      onTagsChange([...selectedTags, tagName]);
    }
  };

  const handleCreateTag = () => {
    if (!newTagName.trim()) return;
    try {
      const newTag = createTag(newTagName);
      setTags(getAllTags());
      setNewTagName("");
      setShowTagMenu(false);
      toast({
        title: t("dashboard_filters.tag_created_title"),
        description: t("dashboard_filters.tag_created_desc", {
          name: newTag.name,
        }),
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("dashboard_filters.tag_create_error");
      toast({
        title: t("dashboard_filters.error_title"),
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    try {
      const newFolder = createFolder(newFolderName);
      setFolders(getAllFolders());
      setNewFolderName("");
      setShowFolderMenu(false);
      toast({
        title: t("dashboard_filters.folder_created_title"),
        description: t("dashboard_filters.folder_created_desc", {
          name: newFolder.name,
        }),
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("dashboard_filters.folder_create_error");
      toast({
        title: t("dashboard_filters.error_title"),
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // 4e palette par type de contenu trouvée dans ce fichier (en plus de
  // getThemeColors/getThemeClasses/doo-*), locale au composant — codée en
  // dur violet/emerald/amber/blue sans lien avec --primary, d'où le bug
  // #3 (Terminé/Archivé restaient bleus sous Corail alors que d'autres
  // boutons du dashboard suivaient déjà le thème). Suit désormais
  // --primary comme les 3 autres palettes retokenisées.
  const getFilterColor = (type: ContentTypeFilter) => {
    switch (type) {
      case "form":
        return {
          activeBg: "bg-violet-500", // color-exempt
          activeBorder: "border-violet-400", // color-exempt
          shadow: "shadow-violet-500/30", // color-exempt
          text: "text-violet-500", // color-exempt
          border: "border-violet-500", // color-exempt
          hoverBg: "hover:bg-violet-600", // color-exempt
          bg: "bg-violet-600", // color-exempt
        };
      case "availability":
        return {
          activeBg: "bg-emerald-500", // color-exempt
          activeBorder: "border-emerald-400", // color-exempt
          shadow: "shadow-emerald-500/30", // color-exempt
          text: "text-emerald-500", // color-exempt
          border: "border-emerald-500", // color-exempt
          hoverBg: "hover:bg-emerald-700", // color-exempt
          bg: "bg-emerald-600", // color-exempt
        };
      case "quizz":
        return {
          activeBg: "bg-amber-500", // color-exempt
          activeBorder: "border-amber-400", // color-exempt
          shadow: "shadow-amber-500/30", // color-exempt
          text: "text-amber-500", // color-exempt
          border: "border-amber-500", // color-exempt
          hoverBg: "hover:bg-amber-700", // color-exempt
          bg: "bg-amber-600", // color-exempt
        };
      default: // date, all, conversations — suit le thème actif
        return {
          activeBg: "bg-primary",
          activeBorder: "border-primary",
          shadow: "shadow-primary/30",
          text: "text-primary",
          border: "border-primary",
          hoverBg: "hover:bg-primary/90",
          bg: "bg-primary",
        };
    }
  };

  const currentTheme = getFilterColor(contentTypeFilter);

  return (
    <div className="mb-6 space-y-4">
      {/* Ligne 1: Recherche + Actions principales (Vue + Sélectionner) */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Barre de recherche */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-10 py-2 bg-muted border border-border text-foreground placeholder:text-muted-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="search-conversations"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={t("dashboard_filters.clear_search")}
              title={t("dashboard_filters.clear_search")}
              data-testid="dashboardfilters-button"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Actions principales: Vue + Sélectionner */}
        <div className="flex items-center gap-3">
          {/* Toggle Grid/Table */}
          <div className="flex items-center gap-1 bg-muted border border-border rounded-lg p-1">
            <button
              data-testid="view-toggle-grid"
              onClick={() => onViewModeChange("grid")}
              className={`p-2 rounded transition-colors ${
                viewMode === "grid"
                  ? `${currentTheme.activeBg} text-white`
                  : "text-muted-foreground hover:bg-accent"
              }`}
              title={t("dashboard_filters.view_grid")}
              aria-label={t("dashboard_filters.view_grid")}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              data-testid="view-toggle-table"
              onClick={() => onViewModeChange("table")}
              className={`hidden md:block p-2 rounded transition-colors ${
                viewMode === "table"
                  ? `${currentTheme.activeBg} text-white`
                  : "text-muted-foreground hover:bg-accent"
              }`}
              title={t("dashboard_filters.view_table")}
              aria-label={t("dashboard_filters.view_table")}
            >
              <Table className="w-4 h-4" />
            </button>
          </div>

          {/* Bouton Sélectionner */}
          {hasItems && (
            <button
              onClick={selectedIdsCount > 0 ? onClearSelection : onSelectAll}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 border ${
                selectedIdsCount > 0
                  ? `${currentTheme.bg} ${currentTheme.hoverBg} text-white ${currentTheme.border}`
                  : "bg-muted hover:bg-accent text-muted-foreground hover:text-accent-foreground border-border"
              }`}
              title={
                selectedIdsCount > 0
                  ? t("dashboard_filters.deselect_all")
                  : t("dashboard_filters.select_all")
              }
              data-testid="selection-toggle-button"
            >
              <CheckSquare className="w-5 h-5" />
              <span className="hidden sm:inline">
                {selectedIdsCount > 0
                  ? t("dashboard_filters.selected_count", {
                      count: selectedIdsCount,
                    })
                  : t("dashboard_filters.select")}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Filters Toggle */}
      <div className="sm:hidden">
        <button
          onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
          className="w-full flex items-center justify-between p-2 border border-border rounded-md bg-muted text-muted-foreground"
          data-testid="dashboardfilters-button"
        >
          <span className="text-sm font-medium">
            {t("dashboard_filters.filters_options")}
          </span>
          <ChevronDown
            className={cn(
              "w-4 h-4 transition-transform duration-200",
              isMobileFiltersOpen ? "rotate-180" : "",
            )}
          />
        </button>
      </div>

      {/* Collapsible Filter Content */}
      <div
        className={cn(
          "space-y-4",
          isMobileFiltersOpen ? "block" : "hidden sm:block",
        )}
      >
        {/* Ligne 2: Filtres type de contenu */}
        {!hideContentTypeFilter && (
          <div className="flex flex-wrap gap-3 items-center">
            {/* Filtres par type de contenu */}
            <div className="flex gap-2 flex-wrap">
              {contentTypeFilters.map(({ value, label, icon }) => {
                const theme = getFilterColor(value);
                return (
                  <button
                    key={value}
                    data-testid={`content-type-filter-${value}`}
                    onClick={() => onContentTypeFilterChange(value)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      contentTypeFilter === value
                        ? `${theme.activeBg} text-white border-2 ${theme.activeBorder} shadow-lg ${theme.shadow} scale-105 font-semibold`
                        : "bg-muted text-muted-foreground hover:bg-accent border border-border hover:border-border/60"
                    }`}
                  >
                    {icon}
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Ligne 3: Filtres statut + Tags + Dossiers */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* Filtres par statut */}
          <div className="flex gap-2 flex-wrap">
            {filters.map((f) => (
              <button
                key={f}
                data-testid={`status-filter-${f}`}
                onClick={() => onFilterChange(f)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  filter === f
                    ? `${currentTheme.activeBg} text-white border-2 ${currentTheme.activeBorder} shadow-lg ${currentTheme.shadow} scale-105 font-semibold`
                    : "bg-muted text-muted-foreground hover:bg-accent border border-border hover:border-border/60"
                }`}
              >
                {f === "all"
                  ? t("dashboard_filters.content_all")
                  : getStatusLabel(f as DashboardPoll["status"])}
              </button>
            ))}
          </div>

          {/* Tags et Dossiers */}
          {/* Filtre par Tags */}
          <div className="relative" ref={tagMenuRef}>
            <button
              onClick={() => setShowTagMenu(!showTagMenu)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                selectedTags.length > 0
                  ? `${currentTheme.activeBg} text-white ${currentTheme.border}`
                  : "bg-muted text-muted-foreground hover:bg-accent border-border"
              }`}
              data-testid="dashboardfilters-button"
            >
              <Tag className="w-4 h-4" />
              {t("dashboard_filters.tags_label")}{" "}
              {selectedTags.length > 0 && `(${selectedTags.length})`}
            </button>

            {showTagMenu && (
              <div
                className="absolute top-full left-0 mt-2 bg-muted border border-border rounded-lg shadow-lg z-50 min-w-[200px] max-h-[300px] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="p-3 border-b border-border">
                  <input
                    type="text"
                    placeholder={t("dashboard_filters.new_tag_placeholder")}
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreateTag();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full px-3 py-2 bg-background border border-border text-foreground rounded text-sm"
                    autoFocus
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateTag();
                    }}
                    className={`mt-2 w-full flex items-center justify-center gap-2 px-3 py-1.5 ${currentTheme.bg} ${currentTheme.hoverBg} text-white rounded text-sm`}
                    data-testid="create-tag-button"
                  >
                    <Plus className="w-3 h-3" />
                    {t("dashboard_filters.create")}
                  </button>
                </div>
                <div className="p-2 space-y-1">
                  {tags.map((tag) => (
                    <label
                      key={tag.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-accent rounded cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTags.includes(tag.name)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleTag(tag.name);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded"
                      />
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="text-sm text-muted-foreground">
                        {tag.name}
                      </span>
                    </label>
                  ))}
                  {tags.length === 0 && (
                    <p className="px-3 py-2 text-sm text-muted-foreground">
                      {t("dashboard_filters.no_tags")}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Filtre par Dossier */}
          <div className="relative" ref={folderMenuRef}>
            <button
              onClick={() => setShowFolderMenu(!showFolderMenu)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                selectedFolderId
                  ? `${currentTheme.activeBg} text-white ${currentTheme.border}`
                  : "bg-muted text-muted-foreground hover:bg-accent border-border"
              }`}
              data-testid="dashboardfilters-button"
            >
              <Folder className="w-4 h-4" />
              {selectedFolderId
                ? folders.find((f) => f.id === selectedFolderId)?.name ||
                  t("dashboard_filters.folder")
                : t("dashboard_filters.all_folders")}
            </button>

            {showFolderMenu && (
              <div
                className="absolute top-full left-0 mt-2 bg-muted border border-border rounded-lg shadow-lg z-50 min-w-[200px]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-3 border-b border-border">
                  <input
                    type="text"
                    placeholder={t("dashboard_filters.new_folder_placeholder")}
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreateFolder();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full px-3 py-2 bg-background border border-border text-foreground rounded text-sm"
                    autoFocus
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreateFolder();
                    }}
                    className={`mt-2 w-full flex items-center justify-center gap-2 px-3 py-1.5 ${currentTheme.bg} ${currentTheme.hoverBg} text-white rounded text-sm`}
                    data-testid="create-folder-button"
                  >
                    <Plus className="w-3 h-3" />
                    {t("dashboard_filters.create")}
                  </button>
                </div>
                <div className="p-2 space-y-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onFolderChange(undefined);
                      setShowFolderMenu(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded text-sm ${
                      !selectedFolderId
                        ? `${currentTheme.bg} text-white`
                        : "text-muted-foreground hover:bg-accent"
                    }`}
                    data-testid="dashboardfilters-tous-les-dossiers"
                  >
                    {t("dashboard_filters.all_folders")}
                  </button>
                  {folders.map((folder) => (
                    <button
                      key={folder.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onFolderChange(folder.id);
                        setShowFolderMenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${
                        selectedFolderId === folder.id
                          ? `${currentTheme.bg} text-white`
                          : "text-muted-foreground hover:bg-accent"
                      }`}
                      data-testid="dashboardfilters-button"
                    >
                      <span>{folder.icon}</span>
                      <span>{folder.name}</span>
                    </button>
                  ))}
                  {folders.length === 0 && (
                    <p className="px-3 py-2 text-sm text-muted-foreground">
                      {t("dashboard_filters.no_folders")}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Tags sélectionnés - affichage */}
          {selectedTags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              {selectedTags.map((tagName) => {
                const tag = tags.find((t) => t.name === tagName);
                return (
                  <span
                    key={tagName}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${currentTheme.bg} text-white`}
                    style={{ backgroundColor: tag?.color || undefined }}
                  >
                    {tagName}
                    <button
                      onClick={() => toggleTag(tagName)}
                      className="hover:bg-black/20 rounded p-0.5"
                      data-testid="dashboardfilters-button"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
